'use client';

import { useTranslations } from 'next-intl';
import { useState, type ReactNode } from 'react';
import { LeftToRightListBulletIcon, Route01Icon } from '@terra/ui-icons';
import { ViewToggleGroup } from '../../../components/view-toggle-group';
import {
  EntityDrawerSection,
  EntityDrawerSections,
  ACTIONS_POSITION,
  type ActionsPosition,
} from '../../../components/entity-drawer';
import type { InternalEnrichedFinding } from '../../../types';
import type { FindingTimelineProps } from '../FindingTimeline';
import { FindingTimeline } from '../FindingTimeline';
import { DrawerCollapsible } from '../../custom-drawer/DrawerCollapsible';
import { Box, VStack } from '@terra/ui-styled-system';
import { MarkdownPreview } from '../../markdown-preview';
import { Skeleton } from '../../../components/skeleton';
import { StructuredPoc } from '../StructuredPoc';

type PocContentProps = {
  finding: InternalEnrichedFinding;
};

function PocContent({ finding }: PocContentProps) {
  if (finding.pocBlocks && finding.pocBlocks.length > 0) {
    return <StructuredPoc blocks={finding.pocBlocks} findingId={finding.id} />;
  }

  return <MarkdownPreview source={finding.poc ?? ''} fontSize={14} />;
}

type FindingDrawerOverviewTabProps = Omit<
  FindingTimelineProps,
  'linkedFindings' | 'chainOrder' | 'onNavigateToFinding' | 'isLoading'
> & {
  finding: InternalEnrichedFinding;
  isDetailLoading?: boolean;
  isTimelineLoading?: boolean;
  isLoading?: boolean;
  pocSectionActions?: ReactNode;
  pocActionsPosition?: ActionsPosition;
  useBoxCollapsibles?: boolean;
  linkedFindings?: InternalEnrichedFinding[];
  onNavigateToFinding?: (findingId: string) => void;
  chainOrder?: string[];
  hideDescription?: boolean;
  hidePoc?: boolean;
  attackPathContent?: ReactNode;
};

type FindingLifecycleView = 'discovery' | 'attackPath';

function isFindingLifecycleView(value: string): value is FindingLifecycleView {
  return value === 'discovery' || value === 'attackPath';
}

function FindingLifecycleSection({
  findingId,
  useBoxCollapsibles,
  attackPathContent,
  isTimelineLoading,
  findingTargetType,
  linkedFindings,
  chainOrder,
  onNavigateToFinding,
  timelineProps,
}: {
  findingId: string;
  useBoxCollapsibles: boolean;
  attackPathContent?: ReactNode;
  isTimelineLoading: boolean;
  findingTargetType: FindingTimelineProps['findingTargetType'];
  linkedFindings?: FindingDrawerOverviewTabProps['linkedFindings'];
  chainOrder?: string[];
  onNavigateToFinding?: (findingId: string) => void;
  timelineProps: Omit<
    FindingTimelineProps,
    'linkedFindings' | 'chainOrder' | 'onNavigateToFinding' | 'isLoading'
  >;
}) {
  const t = useTranslations(
    'designSystem.findings.findingDrawer.tabs.overview',
  );
  const [view, setView] = useState<FindingLifecycleView>('discovery');
  const showAttackPath = view === 'attackPath' && attackPathContent;
  const action = attackPathContent ? (
    <ViewToggleGroup
      size="sm"
      value={view}
      onValueChange={(next) => {
        if (isFindingLifecycleView(next)) {
          setView(next);
        }
      }}
      options={[
        {
          value: 'discovery',
          icon: LeftToRightListBulletIcon,
          ariaLabel: t('discoveryFlowView'),
        },
        {
          value: 'attackPath',
          icon: Route01Icon,
          ariaLabel: t('attackPathView'),
        },
      ]}
    />
  ) : undefined;
  const body = showAttackPath ? (
    attackPathContent
  ) : (
    <FindingTimeline
      isLoading={isTimelineLoading}
      findingTargetType={findingTargetType}
      linkedFindings={linkedFindings?.map((linkedFinding) => ({
        id: linkedFinding.id,
        title: linkedFinding.title ?? null,
        status: linkedFinding.status ?? null,
        severity: linkedFinding.severity ?? null,
        discoveryDate:
          linkedFinding.discoveryDate ?? linkedFinding.createdAt ?? null,
      }))}
      chainOrder={chainOrder}
      onNavigateToFinding={onNavigateToFinding}
      {...timelineProps}
    />
  );

  if (useBoxCollapsibles) {
    return (
      <DrawerCollapsible
        key={`lifecycle-${findingId}`}
        variant="box"
        title={t('findingLifecycle')}
        defaultOpen={true}
        action={action}
      >
        {body}
      </DrawerCollapsible>
    );
  }

  return (
    <EntityDrawerSection title={t('findingLifecycle')} actions={action}>
      {body}
    </EntityDrawerSection>
  );
}

export const FindingDrawerOverviewTab = ({
  finding,
  isDetailLoading = false,
  isTimelineLoading: isTimelineLoadingProp,
  isLoading: legacyTimelineLoading,
  pocSectionActions,
  pocActionsPosition = ACTIONS_POSITION.NextToTitle,
  useBoxCollapsibles = false,
  linkedFindings,
  onNavigateToFinding,
  chainOrder,
  hideDescription = false,
  hidePoc = false,
  attackPathContent,
  ...timelineProps
}: FindingDrawerOverviewTabProps) => {
  const isTimelineLoading =
    isTimelineLoadingProp ?? legacyTimelineLoading ?? false;
  const t = useTranslations(
    'designSystem.findings.findingDrawer.tabs.overview',
  );
  const noDescriptionTranslation = t('noDescription');

  if (useBoxCollapsibles) {
    return (
      <EntityDrawerSections gap="6">
        {!hideDescription ? (
          <DrawerCollapsible
            key={`description-${finding.id}`}
            variant="box"
            title={t('description')}
            defaultOpen={true}
          >
            {isDetailLoading ? (
              <VStack gap={2} w="full" alignItems="stretch">
                <Skeleton h="14px" w="full" borderRadius="sm" />
                <Skeleton h="14px" w="95%" borderRadius="sm" />
                <Skeleton h="14px" w="88%" borderRadius="sm" />
                <Skeleton h="14px" w="full" borderRadius="sm" />
                <Skeleton h="14px" w="70%" borderRadius="sm" />
              </VStack>
            ) : (
              <Box color="fg.muted">
                <MarkdownPreview
                  source={finding.description || noDescriptionTranslation}
                  fontSize={14}
                />
              </Box>
            )}
          </DrawerCollapsible>
        ) : null}
        <FindingLifecycleSection
          findingId={finding.id}
          useBoxCollapsibles
          attackPathContent={attackPathContent}
          isTimelineLoading={isTimelineLoading}
          findingTargetType={finding.targetType}
          linkedFindings={linkedFindings}
          chainOrder={chainOrder}
          onNavigateToFinding={onNavigateToFinding}
          timelineProps={timelineProps}
        />
        {!hidePoc &&
        (finding.poc?.trim() ||
          (finding.pocBlocks && finding.pocBlocks.length > 0)) ? (
          <DrawerCollapsible
            key={`poc-${finding.id}`}
            variant="box"
            title={t('poc')}
            defaultOpen={true}
            action={pocSectionActions}
          >
            <PocContent finding={finding} />
          </DrawerCollapsible>
        ) : null}
      </EntityDrawerSections>
    );
  }

  return (
    <EntityDrawerSections>
      {!hideDescription ? (
        <EntityDrawerSection title={t('description')}>
          {isDetailLoading ? (
            <VStack gap={2} w="full" alignItems="stretch">
              <Skeleton h="14px" w="full" borderRadius="sm" />
              <Skeleton h="14px" w="95%" borderRadius="sm" />
              <Skeleton h="14px" w="88%" borderRadius="sm" />
            </VStack>
          ) : (
            <Box color="fg.muted">
              <MarkdownPreview
                source={finding.description || noDescriptionTranslation}
                fontSize={14}
              />
            </Box>
          )}
        </EntityDrawerSection>
      ) : null}
      <FindingLifecycleSection
        findingId={finding.id}
        useBoxCollapsibles={false}
        attackPathContent={attackPathContent}
        isTimelineLoading={isTimelineLoading}
        findingTargetType={finding.targetType}
        linkedFindings={linkedFindings}
        chainOrder={chainOrder}
        onNavigateToFinding={onNavigateToFinding}
        timelineProps={timelineProps}
      />
      {!hidePoc &&
      (finding.poc?.trim() ||
        (finding.pocBlocks && finding.pocBlocks.length > 0)) ? (
        <EntityDrawerSection
          title={t('poc')}
          actions={pocSectionActions}
          actionsPosition={pocActionsPosition}
        >
          <Box w="full" color="fg.muted">
            <PocContent finding={finding} />
          </Box>
        </EntityDrawerSection>
      ) : null}
    </EntityDrawerSections>
  );
};
